import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  addClientDetailsData,
  fetchClientDetailsData,
  fetchRecentVoyagesData,
  updateClientDetailsData,
} from "../thunks/clientFormUpdateThunk";

import type { clientDetails } from "../../utils/types/type-helper";
import type { Persons } from "@/utils/types/voyages";

interface clientDetailsState {
  clientDetails: clientDetails;
  loading: boolean;
  initialLoading?: boolean;
  data: any;
  notificationHistory: Persons[];
  error: string | null;
  notificationHistoryLoading: boolean;
}

const initialState: clientDetailsState = {
  clientDetails: {} as clientDetails,
  data: {} as clientDetails,
  notificationHistory: [] as Persons[],
  loading: false,
  initialLoading: false,
  error: null,
  notificationHistoryLoading: false,
};

export const clientDetailsSlice = createSlice({
  name: "clientDetails",
  initialState,
  reducers: {
    resetClientDetails: (state) => {
      state.clientDetails = {} as clientDetails;
      state.data = {} as clientDetails;
      state.notificationHistory = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientDetailsData.pending, (state) => {
        state.loading = true;
        state.initialLoading = true;
      })
      .addCase(fetchClientDetailsData.fulfilled, (state, action) => {
        state.clientDetails = action.payload;
        state.loading = false;
        state.initialLoading = false;
      })
      .addCase(fetchClientDetailsData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
        state.initialLoading = false;
      });
    builder
      .addCase(addClientDetailsData.pending, (state) => {
        state.loading = true;
      })
      .addCase(addClientDetailsData.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(addClientDetailsData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
    builder
      .addCase(updateClientDetailsData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateClientDetailsData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateClientDetailsData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
    builder
      .addCase(fetchRecentVoyagesData.pending, (state) => {
        state.notificationHistoryLoading = true;
      })
      .addCase(fetchRecentVoyagesData.fulfilled, (state, action) => {
        // Create a map to aggregate persons by unique identifier (email or id)
        const personMap = new Map<string, any>();

        action.payload.forEach((voyage) => {
          const voyageNumber = voyage.voyage; // Adjust based on your data structure

          (voyage.persons || []).forEach((person) => {
            const key = person.recipient_id 

            if (personMap.has(key)) {
              // Person already exists, add this voyage to their list
              const existing = personMap.get(key);
              existing.voyages = [...(existing.voyages || []), `v.${voyageNumber}`];
            } else {
              // New person, initialize with current voyage
              personMap.set(key, {
                ...person,
                voyages: [`v.${voyageNumber}`],
              });
            }
          });
        });

        // Convert map to array and format voyage display
        state.notificationHistory = Array.from(personMap.values()).map(
          (person) => ({
            ...person,
            voyagesDisplay: person.voyages.join(", "), // e.g., "v.201, v.203"
          })
        );

        state.notificationHistoryLoading = false;
      })
      .addCase(fetchRecentVoyagesData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.notificationHistoryLoading = false;
      });
    addWebSocketCases(builder, "clientDetails", "clientDetails");
  },
});
export const { resetClientDetails } = clientDetailsSlice.actions;

export default clientDetailsSlice.reducer;
